/**
 * @fileoverview Today's jobs, from the one to-do list (D51; owner,
 * 2026-10-15): From yesterday (when something is still worth doing), Today,
 * and Coming up, each a heading with how many ("3 jobs", "1 of 2 done") and
 * the same job cards as the top of Plants (JobCard), in the server's order
 * (by day, danger first within a day). Three at
 * most in a group, then "3 more this week", which shows the rest in place.
 *
 * A job left from yesterday is tapped to choose, in a sheet of the server's
 * choices with their words (missedChoices: do it today, postpone it to
 * tomorrow, skip it, "No harm done; Grow won't ask again"). Jobs postponed
 * today are named under Today with a way to bring each back, and a job
 * postponed to today that is no longer needed is said once, in its own line
 * (moved.gone; owner, 2026-10-17, answer 1). With nothing to do today:
 * "Nothing needs you today." and why.
 *
 * Today's group is framed as Coming up is (B60; owner, 2026-10-22: the loose lines "need to look finished"): its
 * heading is visible, the server's "To do" / "Por hacer" (todo.group.today; the page's own name is Today, so the
 * group takes another word, which replaced the hidden heading of 2026-10-16) with its count at the right; "Add a to-do" and
 * "Plant something", side by side, are its first row (E25, AddAndPlantButtons; AddTodoRow's card before); and the quiet day's answer, the postponed lines, "All done for
 * today." are the rows of one card at its end (QuietCard).
 *
 * "Water today?" (B139, B152) is not drawn here: it is the first thing under the Almanac, above the weather card
 * (WaterSection, drawn by TodayPage). The server holds today's water job(s) out of these groups (water.jobKeys), so a
 * job is drawn once.
 *
 * Coming up shows when the server lists jobs in it (groups.coming.keys is
 * empty when the gardener's Coming up looks no days ahead).
 *
 * An email's link (/today?job=) opens its job's plants and scrolls to it: the
 * job by its key (base|day), else the job of the same base on another day
 * (a link from an email the job has since moved from).
 *
 * In Coming up, each day its jobs are for opens with that day's weather,
 * drawn as Today's is (dayWeather; owner, 2026-10-16: "use only the day that
 * pertains to what is coming up ... style it like today is"), then the jobs
 * of that day.
 *
 * The gardener's own to-dos (D50): their cards are JobCards with Edit in the
 * foot (onEditOwn opens the own to-do sheet); "Add a to-do" and "Plant something" are the Today group's first row (onAdd and onPlant open their sheets; D68 put
 * it under the weather card, B60 framed it as the list's first card, E25 made it two buttons); a to-do just added or edited (shownKey, the server's openKey)
 * is never hidden behind "more" and is scrolled to.
 *
 * Where: TodayPage, in place of To do's list and Coming up's days when the
 * brief has the list. API: none of its own (useTodoMarks makes the marks).
 * Words: the list's own (the brief's todo); today.nothing's quiet line is
 * the brief's.
 *
 * @module TodoGroups
 */

import React from 'react';
import { cn } from '@/lib/utils';
import type { BriefTodo, TodoGroup, TodoJob } from '@/shared/api/todayBrief';
import { BottomPanel } from '@/shared/components/ui/bottom-panel';
import { MarkedJobCard, withOpen } from './MarkedJobCard';
import { QuietCard } from './QuietCard';
import { AddAndPlantButtons } from '@/features/own-todos/AddAndPlantButtons';
import { AnimatedList } from '@/shared/components/ui/AnimatedList';
import type { TodoMarks } from '../lib/useTodoMarks';

/** What the groups show. */
interface TodoGroupsProps {
  todo: BriefTodo;
  /** Marking, from useTodoMarks. */
  marks: TodoMarks;
  /** The job an email's link asked for (its key), opened and scrolled to; null for none. */
  openKey?: string | null;
  /** Why nothing needs the gardener today (the brief's quiet line), under "Nothing needs you today."; null for none. */
  quietWhy?: string | null;
  /** Opens the reminder sheet: "Add a to-do", in the Today group's first row (B60, E25); with onPlant, else no row. */
  onAdd?: () => void;
  /** Opens "Plant something", beside "Add a to-do" (E25). */
  onPlant?: () => void;
  /** Shown between Today and Coming up (a notice or the one tip). */
  between?: React.ReactNode;
  /** A coming day's weather, drawn as Today's ("YYYY-MM-DD"), before that day's jobs in Coming up; null for none. */
  dayWeather?: (date: string) => React.ReactNode;
  /** Opens an own to-do's edit sheet (its card's Edit, D50). */
  onEditOwn?: (job: TodoJob) => void;
  /** The own to-do just added or edited (the server's openKey): shown past "more" and scrolled to; null for none. */
  shownKey?: string | null;
}

/** A group's heading: the serif title, and how many at the right (also the "Water today?" section's, WaterSection). */
export const GROUP_HEADING = 'mb-2 mt-1 flex items-baseline justify-between gap-2 px-1 text-xl font-bold leading-tight font-body';

/**
 * The groups.
 *
 * @param props - TodoGroupsProps.
 */
export const TodoGroups: React.FC<TodoGroupsProps> = ({
  todo, marks, openKey: askedKey = null, quietWhy = null, onAdd, onPlant, between = null, dayWeather, onEditOwn, shownKey = null,
}) => {
  const byKey = React.useMemo(() => new Map(todo.jobs.map((job) => [job.key, job])), [todo.jobs]);
  /** The job the link asked for, by its key, else by its base (the part before "|"): the key it has today. */
  const openKey = React.useMemo((): string | null => {
    if (!askedKey) return null;
    if (byKey.has(askedKey)) return askedKey;
    const base = askedKey.split('|')[0];
    return todo.jobs.find((job) => job.base === base)?.key ?? null;
  }, [askedKey, byKey, todo.jobs]);
  /** The groups shown whole ("3 more this week" tapped). */
  const [whole, setWhole] = React.useState<ReadonlySet<string>>(() => new Set());
  /** The jobs whose plants are open, by key; an email's job opens first. */
  const [opened, setOpened] = React.useState<ReadonlySet<string>>(() => new Set(openKey ? [openKey] : []));
  // The linked job found after the list first came: opened then.
  const [openedFor, setOpenedFor] = React.useState(openKey);
  if (openedFor !== openKey) {
    setOpenedFor(openKey);
    if (openKey) setOpened((current) => new Set(current).add(openKey));
  }
  /** The postponed jobs listed (several, after Show). */
  const [showMoved, setShowMoved] = React.useState(false);
  /** The missed job whose choices are open, or null. */
  const [later, setLater] = React.useState<TodoJob | null>(null);
  const openRef = React.useRef<HTMLDivElement | null>(null);
  const scrolled = React.useRef(false);
  React.useEffect(() => {
    if (!openKey || scrolled.current || !openRef.current) return;
    scrolled.current = true;
    openRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
  /** The own to-do just added or edited, scrolled to once each time it changes. */
  const shownRef = React.useRef<HTMLDivElement | null>(null);
  const shownScrolled = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!shownKey || shownScrolled.current === shownKey || !shownRef.current) return;
    shownScrolled.current = shownKey;
    shownRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  /**
   * One job's card.
   *
   * @param job - The job.
   */
  const card = (job: TodoJob): React.ReactNode => (
    <MarkedJobCard
        job={job}
        words={todo.words}
        marks={marks}
        open={opened.has(job.key)}
        onOpenChange={(open) => setOpened((current) => withOpen(current, job.key, open))}
        onLater={job.missed ? () => setLater(job) : undefined}
        onEdit={job.own && onEditOwn ? () => onEditOwn(job) : undefined}
        cardRef={job.key === openKey ? openRef : job.key === shownKey ? shownRef : undefined}
      />
  );
  /**
   * Jobs as a list that folds a job away and opens a new one smoothly (AnimatedList; owner, 2026-10-16: "clicking
   * mark, postpone, skip causes the screen to jump").
   *
   * @param list - The jobs.
   */
  const listOf = (list: TodoJob[]): React.ReactNode => (
    <AnimatedList className="space-y-2" items={list.map((job) => ({ key: job.key, node: card(job) }))} />
  );

  /**
   * A group: its heading and count, its first jobs, and "3 more this week".
   *
   * @param group - The group.
   * @param tail - What follows its cards (Today's quiet card).
   */
  const groupOf = (group: TodoGroup, tail: React.ReactNode = null, byDay = false): React.ReactNode => {
    const jobs = group.keys.map((key) => byKey.get(key)).filter((job): job is TodoJob => job !== undefined);
    // An email's job is never hidden behind "more", nor an own to-do just added (D50).
    const all = whole.has(group.name) || jobs.slice(todo.shown).some((job) => job.key === openKey || job.key === shownKey);
    const shown = all ? jobs : jobs.slice(0, todo.shown);
    /** The shown jobs by the day they are for, in the list's order (it is by day): each day's weather, then its jobs. */
    const days = shown.reduce<Array<{ date: string; jobs: TodoJob[] }>>((out, job) => {
      const last = out[out.length - 1];
      if (last && last.date === job.due) last.jobs.push(job);
      else out.push({ date: job.due, jobs: [job] });
      return out;
    }, []);
    return (
      <section aria-labelledby={`todo-${group.name}`} key={group.name}>
        <h2 id={`todo-${group.name}`} className={GROUP_HEADING}>
          {group.title}
          {jobs.length > 0 && <span className="font-sans text-sm font-normal text-muted-foreground">{group.count}</span>}
        </h2>
        {/* When you can's promise, in the foreground colour so it reads on every theme's page. */}
        {group.note && jobs.length > 0 && <p className="mb-2 px-1 text-sm text-foreground">{group.note}</p>}
        {/* "Add a to-do" and "Plant something" (D50, D68, B60, E25): the Today list's first row. */}
        {group.name === 'today' && onAdd && onPlant && <AddAndPlantButtons variant="card" onAdd={onAdd} onPlant={onPlant} className="mb-2" />}
        {jobs.length > 0 && (byDay && dayWeather
          ? (
            <div className="space-y-3">
              {days.map((day) => (
                <div key={day.date} className="space-y-2">
                  {dayWeather(day.date)}
                  {listOf(day.jobs)}
                </div>
              ))}
            </div>
          )
          : listOf(shown))}
        {group.more && (
          <button
            type="button"
            onClick={() => setWhole((current) => {
              const next = new Set(current);
              if (all) next.delete(group.name); else next.add(group.name);
              return next;
            })}
            className={cn(
              'block w-full text-center text-[0.9375rem] font-semibold text-primary underline-offset-4 hover:underline',
              // Today's is a row in the list's frame, never loose text between the weather card and Coming up (B60).
              group.name === 'today' ? 'mt-2 min-h-11 rounded-xl border border-border bg-card px-3 py-2.5' : 'py-2.5',
            )}
          >
            {all ? todo.words.fewer : group.more}
          </button>
        )}
        {/* Today's "All done for today." is a row of its quiet card; the other groups say it under their cards. */}
        {group.allDone && group.name !== 'today' && <p className="px-1 pt-2 text-sm font-semibold text-verdict-good">{todo.words.allDone}</p>}
        {tail}
      </section>
    );
  };

  const { yesterday, today, coming, whenever } = todo.groups;
  const todayEmpty = today.keys.length === 0;
  const moved = todo.moved;
  const movedItems = moved?.items ?? [];
  /** The lines for jobs postponed to today that are no longer needed, each said once. */
  const goneLines = moved?.gone ?? [];
  /**
   * Brings one postponed job back to today.
   *
   * @param key - Its key.
   */
  const bringBack = (key: string): void => { void marks.mark(null, { action: 'back', key }); };
  /**
   * The postponed jobs, named (Fable, 2026-10-17: never only a count): one, its line with Bring back; several, "2
   * postponed · Show", then each with its own Bring back. In the foreground colour, its controls a thumb tall. A row
   * of Today's quiet card (QuietCard).
   */
  const movedLine = moved && (goneLines.length > 0 || (moved.text && movedItems.length > 0)) ? (
    <div className="text-sm text-foreground">
      {/* A job postponed to today that is no longer needed (the ZIP moved, the window shut): its one line. */}
      {goneLines.map((line) => <p key={line.key} className="py-1">{line.text}</p>)}
      {moved.text && movedItems.length > 0 && (
      <p className="flex flex-wrap items-center gap-x-2">
        {moved.text}
        {movedItems.length > 1 ? (
          <button type="button" onClick={() => setShowMoved((current) => !current)} aria-expanded={showMoved} className="min-h-11 font-semibold underline underline-offset-2">
            {moved.show}
          </button>
        ) : (
          <button type="button" onClick={() => bringBack(movedItems[0].key)} disabled={marks.busyKey !== null} className="min-h-11 font-semibold underline underline-offset-2">
            {moved.back}
          </button>
        )}
      </p>
      )}
      {movedItems.length > 1 && showMoved && (
        <ul className="divide-y">
          {movedItems.map((item) => (
            <li key={item.key} className="flex flex-wrap items-center gap-x-2 py-1">
              <span className="font-semibold">{item.title}</span>
              <span className="text-muted-foreground">{item.text}</span>
              <button type="button" onClick={() => bringBack(item.key)} disabled={marks.busyKey !== null} className="min-h-11 font-semibold underline underline-offset-2">
                {moved.back}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  ) : null;

  return (
    <div className="space-y-4">
      {yesterday.keys.length > 0 && groupOf(yesterday)}
      {groupOf(today, (
        <QuietCard
          nothing={todayEmpty ? todo.words.nothing : null}
          why={todayEmpty ? quietWhy : null}
          allDone={today.allDone ? todo.words.allDone : null}
          moved={movedLine}
        />
      ))}
      {between}
      {coming.keys.length > 0 && groupOf(coming, null, true)}
      {/* When you can (owner, 2026-10-17): planting windows and once-a-season chores, out of Today's count. */}
      {whenever.keys.length > 0 && groupOf(whenever)}

      {/* A job left from yesterday: the server's choices (today, tomorrow, or skip it), in its words. */}
      <BottomPanel open={later !== null} onOpenChange={(open) => { if (!open) setLater(null); }} title={later?.title ?? ''} wrapTitle>
        <div className="divide-y">
          {(later?.missedChoices ?? []).map((choice) => (
            <button
              key={choice.action}
              type="button"
              disabled={marks.busyKey !== null}
              onClick={() => {
                const job = later;
                setLater(null);
                if (job) void marks.mark(job, { action: choice.action });
              }}
              className={cn('block min-h-12 w-full py-3 text-left font-semibold disabled:opacity-60')}
            >
              {choice.text}
              {choice.sub && <span className="block text-sm font-normal text-muted-foreground">{choice.sub}</span>}
            </button>
          ))}
        </div>
      </BottomPanel>
    </div>
  );
};
